/**
 * 共有先（SNS 等）の URL テンプレート展開（FR-LINK-05）。
 *
 * 要件は「共有先は URL テンプレートとして設定可能とし、既定セットに依存しない」。
 * よって既定は空で、特定サービスの名前もこのファイルには一切現れない。
 * 置換子は `{url}` と `{title}` の 2 つだけ。
 */
import type { ShareTarget } from '../core/options';

/**
 * 共有先で許すスキーム。サーバ側の外部リンク検査（`Overlay\OverlayInput::URL_SCHEMES`）と
 * 同じ集合にして、`javascript:` / `data:` を構造的に締め出す（NFR-SEC-01）。
 */
const SAFE_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

export function expandTarget(target: ShareTarget, url: string, title: string): string {
  return target.url.replace(/\{(url|title)\}/g, (_match, key: string) =>
    encodeURIComponent(key === 'url' ? url : title),
  );
}

/**
 * 展開した共有先 URL を返す。スキームが allowlist の外なら null（＝その共有先は描かない）。
 *
 * 判定は正規表現ではなく URL パーサに任せる。`java&#9;script:` のような分断や大文字小文字、
 * 前後の空白は仕様どおり正規化されてから protocol に現れるため、素朴な前方一致では
 * 抜けられる細工がここでは通らない。相対 URL は `base` へ解決してから見る。
 */
export function safeTargetUrl(
  target: ShareTarget,
  url: string,
  title: string,
  base: string,
): string | null {
  const expanded = expandTarget(target, url, title);
  let protocol: string;
  try {
    protocol = new URL(expanded, base).protocol;
  } catch {
    return null; // base ごと解決できない（＝リンクとして成立しない）
  }
  return SAFE_SCHEMES.has(protocol) ? expanded : null;
}
